import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Home, Login, Shield, ContactSupport } from '@mui/icons-material';

const UnauthorizedPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4" dir="rtl">
      <div className="max-w-2xl w-full text-center">
        
        {/* Header */}
        <div className="mb-10">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full blur-2xl opacity-20"></div>
            <div className="relative">
              <div className="p-6 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-2xl inline-block">
                <Lock className="text-yellow-600 text-5xl" />
              </div>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            الوصول غير مسموح
          </h1>
          
          <div className="h-1.5 w-24 bg-gradient-to-r from-yellow-500 to-orange-500 mx-auto rounded-full mb-6"></div>
          
          <p className="text-xl text-gray-600 mb-2">
            ليس لديك الصلاحيات اللازمة للوصول إلى هذه الصفحة
          </p>
          <p className="text-gray-500 max-w-lg mx-auto">
            قد تحتاج إلى تسجيل الدخول بحساب مختلف أو طلب صلاحيات إضافية من المدير
          </p>
        </div>

        {/* Access Levels */}
        <div className="mb-10 bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="text-primary text-2xl" />
            <h2 className="text-2xl font-bold text-gray-900">مستويات الوصول</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">مستخدم عادي</h3>
              <ul className="text-sm text-gray-600 space-y-1 text-right">
                <li>• التسوق وشراء المنتجات</li>
                <li>• إدارة الطلبات الشخصية</li>
                <li>• الوصول للدعم الفني</li>
              </ul>
            </div>
            
            <div className="p-4 bg-green-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">بائع / صاحب فرع</h3>
              <ul className="text-sm text-gray-600 space-y-1 text-right">
                <li>• إدارة المتجر والمنتجات</li>
                <li>• معالجة طلبات الزبائن</li>
                <li>• الوصول للإحصائيات</li>
              </ul>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">مدير عام</h3>
              <ul className="text-sm text-gray-600 space-y-1 text-right">
                <li>• إدارة النظام بالكامل</li>
                <li>• التحكم في جميع الحسابات</li>
                <li>• الوصول للتقارير المتقدمة</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <Link
            to="/"
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg hover:opacity-90 transition-all duration-300"
          >
            <Home />
            الصفحة الرئيسية
          </Link>
          
          <Link
            to="/login"
            className="flex items-center gap-2 px-6 py-3 bg-white border border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-all duration-300"
          >
            <Login />
            تسجيل الدخول بحساب آخر
          </Link>
          
          <Link
            to="/contact"
            className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300"
          >
            <ContactSupport />
            طلب صلاحيات إضافية
          </Link>
        </div>

        {/* Technical Information */}
        <div className="bg-gray-900 text-white rounded-2xl p-6 mb-8">
          <h3 className="font-bold text-lg mb-3">معلومات تقنية</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-400">رمز الخطأ:</p>
              <p className="font-mono">403_FORBIDDEN</p>
            </div>
            <div>
              <p className="text-gray-400">التوقيت:</p>
              <p>{new Date().toLocaleString('ar-EG')}</p>
            </div>
            <div>
              <p className="text-gray-400">العنوان:</p>
              <p className="truncate">{window.location.pathname}</p>
            </div>
            <div>
              <p className="text-gray-400">الحالة:</p>
              <p className="text-yellow-400">تحتاج صلاحيات أعلى</p>
            </div>
          </div>
        </div>

        {/* Security Message */}
        <div className="mb-8 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="text-green-600" />
            <p className="font-medium text-green-800">أمان النظام يعمل بشكل صحيح</p>
          </div>
          <p className="text-sm text-green-700">
            تم حظر الوصول إلى هذه الصفحة لمنع الوصول غير المصرح به. هذه ميزة أمان وليست خطأ.
          </p>
        </div>

        {/* Contact Support */}
        <div className="space-y-3">
          <p className="text-gray-600">
            إذا كنت تعتقد أن هذا خطأ، يرجى التواصل مع الدعم الفني:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="mailto:support@bazaar-marketplace.com"
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
            >
              support@bazaar-marketplace.com
            </a>
            <a
              href="https://wa.me/201503323006"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
            >
              واتساب الدعم
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-gradient-to-r from-primary to-primary-dark rounded-md"></div>
            <span className="font-bold text-gray-900">بازار ماركت بليس</span>
          </div>
          <p className="text-gray-500 text-sm">
            نظام إدارة متكامل | رقم ضريبي: 752-382-462
          </p>
          <p className="text-gray-400 text-xs mt-2">
            © {new Date().getFullYear()} | مدعوم من CODEOCEAN.TECH
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedPage;